import React from 'react';
import { Eye, MousePointer, Reply } from 'lucide-react';

/**
 * Small inline badge showing email engagement counts.
 * Used in tracked email lists, contact timelines, etc.
 *
 * Props:
 *   opens    - number of times opened
 *   clicks   - number of link clicks
 *   replied  - boolean, whether reply was detected
 *   status   - "sent" | "opened" | "clicked" | "replied"
 *   compact  - if true, show only icons without counts
 */
export default function TrackingBadge({ opens = 0, clicks = 0, replied = false, status, compact = false }) {
    if (!opens && !clicks && !replied && status === 'sent') return null;

    return (
        <span className="inline-flex items-center gap-1.5">
            {/* Opens */}
            {opens > 0 && (
                <span className="inline-flex items-center gap-0.5 text-violet-600" title={`Opened ${opens} time${opens !== 1 ? 's' : ''}`}>
                    <Eye className="w-3.5 h-3.5" />
                    {!compact && <span className="text-xs font-medium">{opens}</span>}
                </span>
            )}

            {/* Clicks */}
            {clicks > 0 && (
                <span className="inline-flex items-center gap-0.5 text-blue-600" title={`${clicks} link click${clicks !== 1 ? 's' : ''}`}>
                    <MousePointer className="w-3.5 h-3.5" />
                    {!compact && <span className="text-xs font-medium">{clicks}</span>}
                </span>
            )}

            {/* Reply */}
            {(replied || status === 'replied') && (
                <span className="inline-flex items-center gap-0.5 text-green-600" title="Replied">
                    <Reply className="w-3.5 h-3.5" />
                </span>
            )}
        </span>
    );
}

/**
 * Status pill for tracked emails.
 */
export function TrackingStatusBadge({ status }) {
    const config = {
        sent: { label: 'Sent', bg: 'bg-stone-100', text: 'text-stone-600' },
        opened: { label: 'Opened', bg: 'bg-violet-50', text: 'text-violet-700' },
        clicked: { label: 'Clicked', bg: 'bg-blue-50', text: 'text-blue-700' },
        replied: { label: 'Replied', bg: 'bg-green-50', text: 'text-green-700' },
        bounced: { label: 'Bounced', bg: 'bg-red-50', text: 'text-red-700' },
    };
    const c = config[status] || config.sent;

    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
            {c.label}
        </span>
    );
}
